// Master Timeline — TTS arrangement surface (block / favIdx / resolver
// model). Renders the chrome via shared <TimelineFrame>; this file owns
// the per-flavour state (refs, zoom hook, playhead drag, chip drag with
// auto-zoom + ghost previews + reorder commit) and the per-flavour visuals
// (chip with waveform, ghosts, drag popup).
//
// The timeline grows into "overtime" when a favorite chip extends past the
// asset boundary (video duration, admin targetDuration, or user-entered
// customDuration). With a loaded video the total is clamped to 2x the
// video duration; without a video there's no cap. An end-marker + diagonal
// overflow shading render only while `totalDur > boundary` (frame-owned).

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import type { Block, Take } from '@shared/types';
import { TIMELINE_GRID_STEP } from '@shared/constants';
import { useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import {
  chipTrackVoiceId,
  distinctActiveVoiceIds,
  previewResolveOverlaps,
  reorderByOffset,
  resolveTimelineOverlapsPerTrack,
} from '@/lib/timeline';
import { voiceColorVar, voiceDisplayName } from '@/lib/voiceMapping';
import { getWaveformSvg } from '@/lib/waveformSvg';
import { useTimelinePreview } from '@/hooks/useTimelinePreview';
import { useTimelineZoom } from '@/hooks/useTimelineZoom';
import { usePlayheadDrag } from '@/hooks/usePlayheadDrag';
import { scheduleVideoScrub } from '@/lib/activeVideo';
import { TimelineFrame, PX_PER_SEC_MAX, type FrameTrack } from './TimelineFrame';
import { favClipDurationSum, lastFavClipEnd, resolveTimelineLength } from '@/lib/timelineLength';
import { isFlexAsset } from '@/lib/assetUi';
import type { MinimapChip } from './TimelineMinimap';
import { chipLabelFits } from './chipLabelFit';
import styles from './Timeline.module.css';

// Drag cap (2× boundary im Standard, max(60 s, 2× Inhalt) im Flex-Modus)
// kommt seit Spec 2026-09-14 als `dragCap` aus resolveTimelineLength.

function snap01(x: number): number {
  return Math.round(x * 10) / 10;
}

function snapUpToGrid(x: number): number {
  return Math.ceil(x / TIMELINE_GRID_STEP) * TIMELINE_GRID_STEP;
}

interface DragState {
  blockIdx: number;
  pointerId: number;
  startClientX: number;
  startOffset: number;
  blockDur: number;
  currentOffset: number;
  ghosts: Array<{ blockIdx: number; offset: number }>;
}

interface Props {
  /** Optional right-aligned slot inside the controls row — used by TTSView
   *  to mount the mixer button + popover alongside play/zoom. */
  mixerSlot?: ReactNode;
  /** Fired after a chip drop reorders the block array. The map maps
   *  old-array-index → new-array-index. TTSView uses it to remap
   *  `activeBlockIdx` so the param panel keeps pointing at the same block. */
  onBlocksReordered?(indexMap: Map<number, number>): void;
  /** simpleMode (Multi-Lingual, Task 9): suppresses ONLY the overflow
   *  shade — passed straight through to TimelineFrame. Default false so
   *  standard projects render byte-identical. */
  hideOverflowShade?: boolean;
}

// --- Chip ------------------------------------------------------------------
// Split out so each chip can own its own async waveform-SVG state. Without
// this split, adding per-chip state inside Timeline's render loop would
// require an external Map + ref dance and a forceUpdate to paint the
// decoded URL — cleaner to give the chip its own component boundary.

interface ChipProps {
  idx: number;
  take: Take;
  widthPx: number;
  leftPx: number;
  isDragged: boolean;
  /** True while ANY chip is being dragged AND this is not the dragged
   *  one — gates pointer events so capture stays on the dragged chip. */
  pointerEventsDisabled: boolean;
  /** Multi-Voice (#173): when set, the chip gets a 3px color-stripe
   *  border-left in this color. Omitted for single-voice assets so they
   *  render bit-for-bit identical to before. */
  voiceColor?: string;
  onPointerDown: (e: React.PointerEvent<HTMLDivElement>, blockIdx: number) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
}

function TimelineChip({
  idx,
  take,
  widthPx,
  leftPx,
  isDragged,
  pointerEventsDisabled,
  voiceColor,
  onPointerDown,
  onPointerMove,
  onPointerUp,
}: ChipProps) {
  const [wfUrl, setWfUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getWaveformSvg(take.buffer).then((url) => {
      if (!cancelled) setWfUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [take.buffer]);

  const cls = [
    styles.chip,
    isDragged ? styles.dragging : '',
    pointerEventsDisabled ? styles.disabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  const labelText = String(idx + 1);
  const showLabel = chipLabelFits(labelText, widthPx);

  return (
    <div
      className={cls}
      style={{
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        backgroundImage: wfUrl ? `url("${wfUrl}")` : undefined,
        backgroundSize: wfUrl ? '100% 100%' : undefined,
        backgroundRepeat: wfUrl ? 'no-repeat' : undefined,
        // Multi-Voice (#173): override --brandcolor for this chip's
        // subtree so the existing chip CSS (background, border,
        // chipLabel pill) all derive from the voice color automatically.
        ...(voiceColor ? ({ ['--brandcolor' as string]: voiceColor } as CSSProperties) : {}),
      }}
      onPointerDown={(e) => onPointerDown(e, idx)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={(e) => e.stopPropagation()}
      data-block-idx={idx}
    >
      {showLabel && <span className={styles.chipLabel}>{labelText}</span>}
    </div>
  );
}

export function Timeline({ mixerSlot, onBlocksReordered, hideOverflowShade }: Props = {}) {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const assetBlocks = useSessionStore((s) => s.assetBlocks);
  const customDurations = useSessionStore((s) => s.customDurations);
  const configAssets = useSessionStore((s) => s.config?.assets);
  const customAssets = useSessionStore((s) => s.customAssets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const updateActiveBlocks = useSessionStore((s) => s.updateActiveBlocks);
  const videoDurations = useVideoStore((s) => s.durations);
  // Bed duration acts as a fallback boundary BEFORE DEFAULT_ASSET_DURATION
  // when no video / target / custom length is set — so loading a bed on a
  // bare asset makes the timeline span the bed automatically.
  const bedDuration = useSessionStore(
    (s) => {
      const beds = s.sfxBeds[s.currentAssetIdx] ?? [];
      if (beds.length === 0) return null;
      return beds.reduce((m, b) => Math.max(m, b.duration || 0), 0);
    },
  );

  const blocks = useMemo<Block[]>(() => {
    return assetBlocks[currentAssetIdx] ?? [];
  }, [assetBlocks, currentAssetIdx]);

  const targetDuration = useMemo(() => {
    const merged = [...(configAssets ?? []), ...customAssets];
    return merged[currentAssetIdx]?.targetDuration ?? null;
  }, [configAssets, customAssets, currentAssetIdx]);

  // Flex-Timeline (Spec 2026-09-14, pro Asset seit Spec 2026-09-21 § 6):
  // keine feste Länge — Anzeige und Zieh-Zone folgen dem letzten Clip.
  // Gilt nur in Multi-Lingual und nur für Assets ohne Video; mit Video ist
  // die Videodauer die Länge, genau wie im Standard-Projekt.
  const flex = useSessionStore((s) => {
    const merged = [...(s.config?.assets ?? []), ...s.customAssets];
    return isFlexAsset(s.config, merged[s.currentAssetIdx]);
  });

  // Active asset's voiceIds — fed into the per-track resolver so cross-voice
  // chips keep their freedom to overlap (#173).
  const assetVoiceIds = useMemo<string[]>(() => {
    const merged = [...(configAssets ?? []), ...customAssets];
    return merged[currentAssetIdx]?.voiceIds ?? [];
  }, [configAssets, customAssets, currentAssetIdx]);

  // Boundary = "official" asset length — video → target → custom → bed → default.
  // Flex: last clip + 1 s tail (min 10 s). dragCap: 2× boundary (standard)
  // bzw. max(60 s, 2× Summe der Chip-Dauern) (flex — nicht das letzte Ende,
  // sonst wächst der Deckel mit jedem Drag).
  const { boundary, dragCap } = useMemo(
    () =>
      resolveTimelineLength({
        flex,
        assetIdx: currentAssetIdx,
        videoDurations,
        targetDuration,
        customDurations,
        bedDuration,
        lastClipEnd: lastFavClipEnd(blocks),
        contentDuration: favClipDurationSum(blocks),
      }),
    [flex, currentAssetIdx, videoDurations, targetDuration, customDurations, bedDuration, blocks],
  );

  // Chip drag state lives above the totalDur memo so the live `currentOffset`
  // can flow into the timeline-width computation during drag — that's what
  // gives the smooth "track grows as you drag past the boundary" behavior.
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  // Auto-zoom toggle — when ON, dragging a chip zooms the timeline so the
  // chip occupies ~15% of the visible width and stays centered. On
  // pointerup/cancel we always snap back to sticky-fit (`pxPerSecManual = -1`)
  // — the previous zoom level is intentionally NOT remembered.
  const [autoZoom, setAutoZoom] = useState<boolean>(false);

  // Wrap geometry — must be declared before the totalDur memo so the
  // memo can use wrapW for the min-width visual-pin closed-form
  // (offset / (1 − 24 / wrapW)). The scrollWrapRef is only read by
  // chip handlers downstream, so its declaration position doesn't
  // matter; we still co-locate it here for readability.
  const scrollWrapRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [wrapW, setWrapW] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const handleDragActiveRef = useRef(false);

  // totalDur = max chip-end, hard-capped at 2x boundary (or unbounded when
  // boundary is 0). During a drag, the dragged block's live offset is used
  // instead of its committed `b.offset`, so the timeline visually grows in
  // real time while the user pushes a chip into overtime.
  //
  // Min-width visual pin (vanilla parity, legacy lines 6515–6524): the chip
  // element is min-width-pinned at 24px so a thin chip's visual right edge
  // can spill past its logical end. At sticky-fit, pxPerSec = wrapW/totalDur,
  // so min-width = 24px translates to 24*totalDur/wrapW seconds. Requiring
  // the chip's visual right to stay within the wrap (offset + 24*totalDur/
  // wrapW ≤ totalDur) yields the closed-form  totalDur ≥ offset/(1 − 24/
  // wrapW). Applied unconditionally — at manual zoom this only inflates
  // track width by a few px, well below user perception against a track
  // the user has already chosen to zoom.
  const { totalDur, inOvertime } = useMemo(() => {
    let maxEnd = boundary;
    const visualPinFactor = wrapW > 24 ? 1 / (1 - 24 / wrapW) : 0;
    blocks.forEach((b, i) => {
      if (b.favIdx < 0) return;
      const take = b.takes[b.favIdx];
      if (!take) return;
      const dur = take.duration || 0;
      const off = drag && drag.blockIdx === i ? drag.currentOffset : b.offset || 0;
      const end = off + dur;
      if (end > maxEnd) maxEnd = end;
      if (visualPinFactor > 0) {
        const pinned = off * visualPinFactor;
        if (pinned > maxEnd) maxEnd = pinned;
      }
    });
    const maxAllowed = dragCap > 0 ? dragCap : Infinity;
    const total = Math.max(Math.min(maxEnd, maxAllowed), 1);
    // Flex kennt kein Overtime: kein End-Marker, keine Schraffur.
    return { totalDur: total, inOvertime: !flex && total > boundary + 0.01 };
  }, [blocks, boundary, dragCap, flex, drag, wrapW]);

  // --- Refs + measurement note --------
  // scrollWrapRef + trackRef + wrapW + scrollLeft + handleDragActiveRef
  // are declared above (between the drag state and the totalDur memo) so
  // the memo can use wrapW. Gate flag handleDragActiveRef is flipped on
  // by `<TimelineMinimap>` while a handle drag is active so passive scroll
  // updates don't race the drag. Mirrors vanilla's `_minimapHandleDragging`.

  // Sticky-fit zoom model — see useTimelineZoom for the full description.
  // `pxPerSecManual === -1` is the sentinel for "sticky-fit active";
  // `stickyFit` and `pxPerSec` are derived. Re-fit-on-shrink is wired to
  // `boundary` so a chip dragged into overtime + back doesn't re-snap zoom
  // (totalDur would re-trigger; boundary doesn't).
  const {
    pxPerSec,
    fitPxPerSec,
    stickyFit,
    pxPerSecManual,
    setPxPerSecManual,
    zoomIn,
    zoomOut,
    zoomFit,
    zoomFitTo,
  } = useTimelineZoom({
    wrapW,
    totalDur,
    scrollWrapRef,
    shrinkOnBoundary: boundary,
  });

  const trackWidth = Math.max(wrapW || 0, pxPerSec * totalDur || 0);

  // --- Playhead + preview playback ---------------------------------------
  const { play, stop, seek, isPlaying, playheadSec } = useTimelinePreview(
    blocks,
    totalDur,
    // Beds resolve against the display boundary, NOT the voice-widened
    // totalDur — otherwise preview placement diverges from export in overtime.
    boundary,
  );

  useEffect(() => {
    stop();
    seek(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAssetIdx]);

  // playFromStart — wired through the frame to the playback singleton.
  // stop() then play(0) so a video-click never resumes from a stale
  // playhead position (mirrors the seek() restart-from-N path but
  // unconditionally starts from 0 even when currently paused).
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const playFromStart = useCallback(() => {
    stop();
    void play(0);
  }, [stop, play]);

  // --- Chip drag ---------------------------------------------------------
  const onChipPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, blockIdx: number) => {
      const block = blocks[blockIdx];
      if (!block) return;
      const take = block.takes[block.favIdx];
      if (!take) return;
      const dur = take.duration || 0;
      const target = e.currentTarget;
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        /* no-op */
      }
      e.preventDefault();
      // FB#1: stop timeline playback the moment a chip drag starts. Without
      // this, audio keeps playing from the OLD chip position while the chip
      // moves visually — confusing because playback won't resume from the
      // new position. Simplest UX: kill playback so the user re-plays after
      // the drop.
      if (isPlayingRef.current) stop();
      setDrag({
        blockIdx,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startOffset: block.offset || 0,
        blockDur: dur,
        currentOffset: block.offset || 0,
        ghosts: [],
      });

      // Auto-zoom: capture current zoom + push to a level where the chip
      // occupies ~15% of the visible scrollWrap width. We only enter this
      // branch when the user has explicitly enabled the toggle. The target
      // px/sec is floored at fitPxPerSec so auto-zoom never zooms OUT past
      // show-all (e.g. for very long chips where 15% would resolve to a
      // smaller-than-fit value).
      if (autoZoom && dur > 0 && wrapW > 0) {
        // No zoom-state snapshot — release always snaps back to sticky-fit.
        const targetPx = Math.max(
          fitPxPerSec,
          Math.min(PX_PER_SEC_MAX, (wrapW * 0.15) / dur),
        );
        // Capture the click's timeline-time + viewport-X BEFORE the zoom
        // commits — after zoom we anchor newScrollLeft so this same
        // timeline-time lines up with the same viewport-X. Result: the
        // pixel under the cursor stays under the cursor across the zoom,
        // so a chip clicked on the LEFT stays on the left, etc.
        const wrap = scrollWrapRef.current;
        let anchorTimelineSec: number | null = null;
        let anchorViewportX: number | null = null;
        if (wrap && pxPerSec > 0) {
          const wrapRect = wrap.getBoundingClientRect();
          const viewportX = e.clientX - wrapRect.left;
          anchorViewportX = viewportX;
          // Constrain the anchor to the chip's actual time range. At
          // low zoom levels the chip's visual width is min-width-pinned
          // at 24px so a 3s clip on a 5min timeline (visual: 24px,
          // logical: 12px) lets the user click 12px past the actual
          // right edge while still landing on the chip element. Without
          // this clamp the post-zoom cursor would honour that "phantom"
          // timeline position and land just past the chip rather than
          // on it.
          const rawAnchor = (viewportX + wrap.scrollLeft) / pxPerSec;
          const chipStart = block.offset || 0;
          anchorTimelineSec = Math.max(
            chipStart,
            Math.min(chipStart + dur, rawAnchor),
          );
        }
        flushSync(() => {
          setPxPerSecManual(targetPx);
        });
        if (
          wrap &&
          anchorTimelineSec !== null &&
          anchorViewportX !== null
        ) {
          // Visibility clamp: on long timelines the chip is only a few
          // pixels wide at sticky-fit, so a tiny cursor offset translates
          // to many seconds of post-zoom timeline offset and the raw
          // anchor formula can land the scroll so far off the chip that
          // the chip itself is out of view. Clamp the result so the chip
          // stays inside the new viewport with a small breathing margin.
          const want = anchorTimelineSec * targetPx - anchorViewportX;
          const chipLeftPx = (block.offset || 0) * targetPx;
          const chipRightPx = chipLeftPx + dur * targetPx;
          const marginPx = wrapW * 0.04;
          const minScroll = Math.max(0, chipRightPx + marginPx - wrapW);
          const maxScrollForChip = Math.max(minScroll, chipLeftPx - marginPx);
          const maxScroll = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
          const clamped = Math.max(minScroll, Math.min(maxScrollForChip, want));
          wrap.scrollLeft = Math.max(0, Math.min(maxScroll, clamped));
        }
      }
    },
    [blocks, autoZoom, wrapW, fitPxPerSec, pxPerSec, setPxPerSecManual, stop],
  );

  const onChipPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      if (!trackRef.current || pxPerSec <= 0) return;
      // Multi-Voice (#173): vertical drag is intentionally absent — voice
      // change happens via the block-params VoicePicker, not by dragging a
      // chip across tracks. We read only clientX; the chip's Y position is
      // derived from chipTrackVoiceId() on render.
      const dx = e.clientX - d.startClientX;
      let offset = d.startOffset + dx / pxPerSec;
      offset = snapUpToGrid(offset);
      offset = Math.max(0, offset);
      // Hard cap: chip cannot be dragged past the drag zone.
      const maxAllowed = dragCap > 0 ? dragCap : Infinity;
      const maxOff = Math.max(0, maxAllowed - d.blockDur);
      if (offset > maxOff) offset = snap01(maxOff);
      // Multi-Voice (#173): ghost preview must mirror the per-track resolver
      // used at commit time. Restrict the dry-run to same-voice-track chips
      // so cross-track chips never get a dashed outline; remap indices back
      // to the global blocks array for ghost rendering.
      const draggedVoice = chipTrackVoiceId(blocks[d.blockIdx], assetVoiceIds);
      const localToGlobal: number[] = [];
      const sameTrackBlocks: Block[] = [];
      for (let i = 0; i < blocks.length; i++) {
        if (chipTrackVoiceId(blocks[i], assetVoiceIds) === draggedVoice) {
          localToGlobal.push(i);
          sameTrackBlocks.push(blocks[i]);
        }
      }
      const localPinIdx = localToGlobal.indexOf(d.blockIdx);
      const ghosts = previewResolveOverlaps(
        sameTrackBlocks,
        localPinIdx,
        offset,
        totalDur,
      )
        .map((g) => ({ blockIdx: localToGlobal[g.blockIdx], offset: g.offset }))
        .filter((g) => {
          if (g.blockIdx === d.blockIdx) return false;
          const cur = blocks[g.blockIdx]?.offset ?? 0;
          return Math.abs(cur - g.offset) >= 0.05;
        });
      setDrag({ ...d, currentOffset: offset, ghosts });

      // Live video scrub during chip drag — restores legacy L7359.
      // rAF-throttled + seeking-guarded inside scheduleVideoScrub so a
      // streamed WebDAV source survives fast pointermove without piling
      // up Range requests on the proxy.
      scheduleVideoScrub(offset);

      // No scrollLeft override during auto-zoom drag — the chip's visual
      // pixel offset is `offset * pxPerSec`, which shifts by exactly `dx`
      // each move (since `offset = startOffset + dx / pxPerSec`). With
      // scrollLeft constant, the chip naturally stays under the cursor.
      // The previous center-follow snapped the chip into the viewport
      // centre on every move, which contradicts the click-anchored
      // mouse-position behaviour set up in onChipPointerDown.
    },
    [blocks, pxPerSec, totalDur, dragCap, assetVoiceIds],
  );

  const onChipPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const target = e.currentTarget;
      try {
        target.releasePointerCapture(e.pointerId);
      } catch {
        /* no-op */
      }
      let finalOffset = d.currentOffset;
      if (typeof e.clientX === 'number' && pxPerSec > 0) {
        const dx = e.clientX - d.startClientX;
        let off = d.startOffset + dx / pxPerSec;
        off = snapUpToGrid(off);
        off = Math.max(0, off);
        const maxAllowed = dragCap > 0 ? dragCap : Infinity;
        const maxOff = Math.max(0, maxAllowed - d.blockDur);
        if (off > maxOff) off = snap01(maxOff);
        finalOffset = off;
      }

      const captured: { map: Map<number, number> | null } = { map: null };
      updateActiveBlocks((prev) => {
        if (!prev[d.blockIdx]) return prev;
        const pinned = prev.slice();
        pinned[d.blockIdx] = {
          ...pinned[d.blockIdx],
          offset: finalOffset,
          pinned: true,
        };
        const resolved = resolveTimelineOverlapsPerTrack(pinned, assetVoiceIds, d.blockIdx, totalDur, dragCap);
        // Sync block-array order to new visual left-to-right chip order so
        // the block list mirrors the timeline. indexMap is captured for the
        // parent to remap activeBlockIdx after this state commit.
        const { blocks: reordered, indexMap } = reorderByOffset(resolved);
        captured.map = indexMap;
        return reordered;
      });
      setDrag(null);
      if (captured.map && onBlocksReordered) {
        // Only fire when the reorder actually shifted at least one block.
        let changed = false;
        captured.map.forEach((newIdx, oldIdx) => {
          if (newIdx !== oldIdx) changed = true;
        });
        if (changed) onBlocksReordered(captured.map);
      }

      // Auto-zoom release always snaps back to sticky-fit — we intentionally
      // do NOT remember the pre-drag zoom level. Done in both pointerUp and
      // pointerCancel paths via the same handler.
      if (autoZoom) {
        setPxPerSecManual(-1);
      }
    },
    [pxPerSec, totalDur, dragCap, updateActiveBlocks, onBlocksReordered, autoZoom, setPxPerSecManual, assetVoiceIds],
  );

  // --- Zoom controls -----------------------------------------------------
  // zoomIn/zoomOut/zoomFit/zoomFitTo come from useTimelineZoom. zoomFitBlocks
  // is local because the source of the bounding-box (favorite chips) is
  // TTS-specific; it just delegates to zoomFitTo with a {leftSec, span}.
  const zoomFitBlocks = useCallback(() => {
    if (!wrapW) return;
    let boxStart = Infinity;
    let boxEnd = 0;
    blocks.forEach((b) => {
      if (b.favIdx < 0) return;
      const take = b.takes[b.favIdx];
      if (!take) return;
      const off = b.offset || 0;
      const dur = take.duration || 0;
      if (off < boxStart) boxStart = off;
      if (off + dur > boxEnd) boxEnd = off + dur;
    });
    if (!Number.isFinite(boxStart) || boxEnd <= boxStart) {
      // No chips → fall back to plain fit.
      zoomFit();
      return;
    }
    zoomFitTo({ leftSec: boxStart, span: boxEnd - boxStart });
  }, [blocks, wrapW, zoomFit, zoomFitTo]);

  // --- Playhead drag -----------------------------------------------------
  // Whole-shaft drag is owned by usePlayheadDrag. Caller wires only
  // `onPointerDown`; the hook attaches window pointermove/up listeners
  // for the duration of the gesture so the cursor can roam outside the
  // track without losing the drag.
  const {
    playheadDrag,
    onPointerDown: onPlayheadPointerDown,
  } = usePlayheadDrag({
    trackRef,
    pxPerSec,
    totalDur,
    playheadSec,
    isPlaying,
    stop,
    seek,
  });

  const displayPlayheadSec = playheadDrag ?? playheadSec;

  // --- Minimap -----------------------------------------------------------
  // Drag-handlers + own width measurement live inside `<TimelineMinimap>`.
  // We expose the chip-data shape it needs via a memo here so the Timeline
  // owns "what" to render, the Minimap owns "how" + the gestures.
  //
  // Live drag offset is mirrored into the minimap chip so the mini-block
  // tracks alongside the main timeline chip in real time.
  const minimapChips = useMemo<MinimapChip[]>(
    () =>
      blocks.flatMap((b, i) => {
        if (b.favIdx < 0) return [];
        const take = b.takes[b.favIdx];
        if (!take) return [];
        const dur = take.duration || 0;
        const isDragged = drag?.blockIdx === i;
        const off = isDragged ? drag!.currentOffset : b.offset || 0;
        // voiceId for multi-voice stacking (#173). Computed via the
        // chipTrackVoiceId helper so it matches the main-track row split.
        const vid = chipTrackVoiceId(b, assetVoiceIds);
        return [{ offset: off, duration: dur, voiceId: vid }];
      }),
    [blocks, drag, assetVoiceIds],
  );

  // --- Multi-Voice (#173) ------------------------------------------------
  // distinctActiveVoiceIds returns the active voiceIds in asset.voiceIds
  // order (stale voiceIds — admin removed a voice mid-session — land at
  // the end so they still get a track row). isMultiVoice gates ALL
  // multi-track visuals: the chip color-border + Voice-N label AND the
  // stacked track layout. Single-voice assets continue to render through
  // the legacy single-track path bit-for-bit identical.
  const activeVoiceIds = useMemo(
    () => distinctActiveVoiceIds(blocks, assetVoiceIds),
    [blocks, assetVoiceIds],
  );
  const isMultiVoice = assetVoiceIds.length >= 2 && activeVoiceIds.length >= 2;

  // Map blockIdx → voiceId so chip-render + ghost-render share the same
  // lookup without re-running chipTrackVoiceId multiple times per frame.
  const chipVoiceById = useMemo(() => {
    const m: Record<number, string> = {};
    blocks.forEach((b, i) => {
      m[i] = chipTrackVoiceId(b, assetVoiceIds);
    });
    return m;
  }, [blocks, assetVoiceIds]);

  // Render-helpers — produce ReactNodes per-block so the multi-track
  // partition + single-track flat-list paths can share the same chip JSX.
  const renderChip = (b: Block, i: number): ReactNode => {
    if (b.favIdx < 0) return null;
    const take = b.takes[b.favIdx];
    if (!take) return null;
    const dur = take.duration || 0;
    const isDragged = drag?.blockIdx === i;
    const offset = isDragged ? drag!.currentOffset : b.offset || 0;
    const width = Math.max(24, dur * pxPerSec);
    const chipVoice = chipVoiceById[i];
    return (
      <TimelineChip
        key={`chip-${i}`}
        idx={i}
        take={take}
        widthPx={width}
        leftPx={offset * pxPerSec}
        isDragged={isDragged}
        pointerEventsDisabled={drag !== null && !isDragged}
        voiceColor={
          isMultiVoice ? voiceColorVar(chipVoice, assetVoiceIds) : undefined
        }
        onPointerDown={onChipPointerDown}
        onPointerMove={onChipPointerMove}
        onPointerUp={onChipPointerUp}
      />
    );
  };

  const renderGhost = (g: { blockIdx: number; offset: number }): ReactNode => {
    const b = blocks[g.blockIdx];
    const take = b?.takes[b.favIdx];
    const dur = take?.duration || 0;
    const width = Math.max(24, dur * pxPerSec);
    return (
      <div
        key={`ghost-${g.blockIdx}`}
        className={styles.ghost}
        style={{
          left: `${g.offset * pxPerSec}px`,
          width: `${width}px`,
        }}
      />
    );
  };

  // Multi-track payload — one FrameTrack per active voice. Ghosts get
  // partitioned by their target block's voiceId so they always sit in
  // the same row as the chip they preview.
  const multiTracks = useMemo<FrameTrack[] | undefined>(() => {
    if (!isMultiVoice) return undefined;
    return activeVoiceIds.map((vid) => {
      const officialIdx = assetVoiceIds.indexOf(vid);
      const numberLabel = officialIdx >= 0 ? String(officialIdx + 1) : '?';
      const numberColor = voiceColorVar(vid, assetVoiceIds);
      const numberTitle = voiceDisplayName(vid, assetVoiceIds, configVoices ?? []);
      const trackChildren = (
        <>
          {blocks.map((b, i) =>
            chipVoiceById[i] === vid ? renderChip(b, i) : null,
          )}
          {drag?.ghosts
            .filter((g) => chipVoiceById[g.blockIdx] === vid)
            .map((g) => renderGhost(g))}
        </>
      );
      return {
        key: vid,
        numberLabel,
        numberColor,
        numberTitle,
        trackChildren,
      };
    });
    // renderChip + renderGhost intentionally not in deps — they close over
    // the same state we list explicitly below. Including them would
    // re-create multiTracks on every render and defeat the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMultiVoice,
    activeVoiceIds,
    assetVoiceIds,
    blocks,
    chipVoiceById,
    drag,
    pxPerSec,
    configVoices,
  ]);

  return (
    <TimelineFrame
      scrollWrapRef={scrollWrapRef}
      trackRef={trackRef}
      setWrapW={setWrapW}
      setScrollLeft={setScrollLeft}
      scrollLeft={scrollLeft}
      wrapW={wrapW}
      handleDragActiveRef={handleDragActiveRef}
      totalDur={totalDur}
      boundary={boundary}
      inOvertime={inOvertime}
      hideOverflowShade={hideOverflowShade}
      limitSec={flex ? targetDuration : null}
      pxPerSec={pxPerSec}
      fitPxPerSec={fitPxPerSec}
      trackWidth={trackWidth}
      pxPerSecManual={pxPerSecManual}
      stickyFit={stickyFit}
      setPxPerSecManual={setPxPerSecManual}
      zoomIn={zoomIn}
      zoomOut={zoomOut}
      zoomFit={zoomFit}
      playheadSec={playheadSec}
      displayPlayheadSec={displayPlayheadSec}
      isPlaying={isPlaying}
      isPlayheadDragging={playheadDrag !== null}
      onPlayheadPointerDown={onPlayheadPointerDown}
      play={play}
      stop={stop}
      seek={seek}
      playFromStart={playFromStart}
      mixerSlot={mixerSlot}
      fitContentLabel="Fit Blocks"
      fitContentTitle="Fit blocks — zoom + scroll so all chips fill the view"
      fitContentAriaLabel="Fit blocks"
      onFitContent={zoomFitBlocks}
      autoZoom={autoZoom}
      onToggleAutoZoom={() => setAutoZoom((v) => !v)}
      minimapChips={minimapChips}
      minimapActiveVoiceIds={isMultiVoice ? activeVoiceIds : undefined}
      minimapAssetVoiceIds={isMultiVoice ? assetVoiceIds : undefined}
      isDragging={drag !== null}
      multiTracks={multiTracks}
    >
      {/* Single-track mode: chips + ghosts as direct children of .track.
          Multi-track mode: per-track chips + ghosts live inside
          multiTracks[i].trackChildren; only the drag-popup is passed here
          as a cross-track overlay. */}
      {!isMultiVoice && blocks.map((b, i) => renderChip(b, i))}
      {!isMultiVoice && drag?.ghosts.map((g) => renderGhost(g))}

      {/* Drag-time offset popup, anchored to the pinned chip's offset.
          In multi-track mode this spans .trackArea (the full stacked
          chip-height); in single-track it sits inside .track as before. */}
      {drag && (
        <div
          className={styles.dragPopup}
          style={{ left: `${drag.currentOffset * pxPerSec}px` }}
        >
          {drag.currentOffset.toFixed(1)}s
        </div>
      )}
    </TimelineFrame>
  );
}
